import { calculateTotalAmount } from "../../../shared/utils/areaCalculation";
import { type NutrientGroup } from "./nutrientPlots";
import {
  type NormalizedCpApplication,
  type NormalizedCpTreatment,
} from "../components/CPBiological/normalizeCpAssignments";

interface BiologicalTrialPlot {
  replicants?: unknown[] | Record<string, unknown> | null;
  plot?: {
    id?: number | string;
    isStripPlot?: boolean;
    totalStrips?: number;
    rows?: number;
    columns?: number;
  };
}

interface TargetLocation {
  plotGroupId: number;
  plotCellId: number;
  areaHa: number;
  replicateMultiplier: number;
}

interface BiologicalPlotProduct {
  variety: string;
  rate: number;
  quantity: number;
  measure_unit: string;
}

interface BiologicalPlotAssignment {
  plot_group_id: number;
  plot_cell_id: number;
  color: string;
  products: BiologicalPlotProduct[];
}

interface BiologicalUntreatedPlot {
  plot_group_id: number;
  plot_cell_id: number;
}

interface BiologicalProductTotal {
  variety: string;
  value: number;
  measure_unit: string;
}

interface BiologicalGroupTotals {
  plot_group_id: number;
  by_product: BiologicalProductTotal[];
  by_application: BiologicalProductTotal[][];
}

export interface BiologicalTreatmentAssignmentsPayload {
  biological_calculated_totals?: BiologicalGroupTotals[];
  biological_treatment_assignments: Array<{
    application_index: number;
    treatments?: Array<{
      treatment_index: number;
      assignments: BiologicalPlotAssignment[];
    }>;
    untreated?: BiologicalUntreatedPlot[];
    reset_untreated?: boolean;
  }>;
}

interface BuildBiologicalAssignmentsPayloadArgs {
  designGroups: NutrientGroup[];
  trialPlots: BiologicalTrialPlot[];
  applications: NormalizedCpApplication[];
  treatments: NormalizedCpTreatment[];
  applicationIndex: number;
  treatmentIndex: number;
  selectedCellIds: string[];
  untreatedCellIds?: string[];
  color: string;
}

interface BuildBiologicalAssignmentsSnapshotArgs
  extends Pick<
    BuildBiologicalAssignmentsPayloadArgs,
    "designGroups" | "trialPlots" | "applications" | "treatments"
  > {
  throughApplicationIndex?: number;
}

interface BiologicalProductDefinition {
  variety?: unknown;
  rate?: unknown;
  measure_unit?: unknown;
}

const buildTargetLocations = (
  designGroups: NutrientGroup[],
  trialPlots: BiologicalTrialPlot[]
): Map<string, TargetLocation> => {
  const locations = new Map<string, TargetLocation>();

  designGroups.forEach((group) => {
    const plotGroupId = trialPlots.findIndex(
      (trialPlot) => String(trialPlot.plot?.id) === group.id
    );
    if (plotGroupId < 0) {
      throw new Error(`Unknown biological plot group: ${group.id}`);
    }

    const backendGroupOffset = trialPlots
      .slice(0, plotGroupId)
      .reduce(
        (offset, entry) =>
          offset +
          (entry.plot?.isStripPlot
            ? entry.plot.totalStrips ??
              (entry.plot.rows ?? 1) * (entry.plot.columns ?? 1)
            : 1),
        0
      );
    const isStripGroup = trialPlots[plotGroupId].plot?.isStripPlot === true;
    const replicants = trialPlots[plotGroupId].replicants;
    const replicateMultiplier =
      isStripGroup || !replicants ? 1 : 1 + Object.keys(replicants).length;

    group.plots.forEach((plot, plotCellId) => {
      locations.set(plot.id, {
        plotGroupId: backendGroupOffset + (isStripGroup ? plotCellId : 0),
        plotCellId: isStripGroup ? 0 : plotCellId,
        areaHa: plot.areaHa,
        replicateMultiplier,
      });
    });
  });

  return locations;
};

const getProductsForTreatment = (
  treatments: NormalizedCpTreatment[],
  treatmentIndex: number,
  areaHa: number
): BiologicalPlotProduct[] => {
  const definition = treatments.find(
    (treatment) => treatment.treatmentId === treatmentIndex
  )?.definition;
  const configuredProducts =
    typeof definition === "object" && definition !== null
      ? (definition as { products?: BiologicalProductDefinition[] }).products
      : undefined;

  if (!Array.isArray(configuredProducts) || configuredProducts.length === 0) {
    throw new Error(`Treatment ${treatmentIndex} has no configured products`);
  }

  return configuredProducts.map((product) => {
    const variety =
      typeof product.variety === "string" ? product.variety.trim() : "";
    const measureUnit =
      typeof product.measure_unit === "string"
        ? product.measure_unit.trim()
        : "";
    const rate = Number(product.rate);

    if (!variety || !measureUnit || !Number.isFinite(rate)) {
      throw new Error(`Treatment ${treatmentIndex} has invalid product data`);
    }

    return {
      variety,
      rate,
      quantity: calculateTotalAmount(rate, areaHa, measureUnit),
      measure_unit: measureUnit,
    };
  });
};

const addTotal = (
  totals: Map<string, BiologicalProductTotal>,
  product: BiologicalPlotProduct,
  multiplier: number
): void => {
  const previous = totals.get(product.variety);
  totals.set(product.variety, {
    variety: product.variety,
    value: (previous?.value ?? 0) + product.quantity * multiplier,
    measure_unit: previous?.measure_unit ?? product.measure_unit,
  });
};

const savedProducts = (raw: unknown): BiologicalPlotProduct[] | undefined => {
  if (typeof raw !== "object" || raw === null || !("products" in raw)) {
    return undefined;
  }
  if (!Array.isArray(raw.products) || raw.products.length === 0)
    return undefined;
  return raw.products.every(
    (product: unknown) =>
      typeof product === "object" &&
      product !== null &&
      "variety" in product &&
      typeof product.variety === "string" &&
      "rate" in product &&
      typeof product.rate === "number" &&
      "quantity" in product &&
      typeof product.quantity === "number" &&
      Number.isFinite(product.quantity) &&
      "measure_unit" in product &&
      typeof product.measure_unit === "string"
  )
    ? (raw.products as BiologicalPlotProduct[])
    : undefined;
};

const buildCalculatedTotals = (
  locations: Map<string, TargetLocation>,
  applications: NormalizedCpApplication[],
  treatments: NormalizedCpTreatment[],
  applicationIndex: number | undefined,
  treatmentIndex: number | undefined,
  selectedAssignments: Map<string, BiologicalPlotProduct[]>,
  untreatedIds: Set<string>,
  affectedGroupIds: number[] = []
): BiologicalGroupTotals[] => {
  const current = applications.find(
    (entry) => entry.index === applicationIndex
  );
  const affectedGroups = new Set<number>(affectedGroupIds);
  locations.forEach((location, cellId) => {
    const assignment = current?.assignments[cellId];
    if (
      selectedAssignments.has(cellId) ||
      untreatedIds.has(cellId) ||
      (assignment?.kind === "assigned" &&
        assignment.treatmentId === treatmentIndex)
    ) {
      affectedGroups.add(location.plotGroupId);
    }
  });
  const applicationCount = Math.max(
    (applicationIndex ?? -1) + 1,
    ...applications.map((entry) => entry.index + 1)
  );

  return [...affectedGroups]
    .sort((first, second) => first - second)
    .flatMap((plotGroupId) => {
      const hasUnresolvedCells = applications.some((application) =>
        [...locations].some(
          ([cellId, location]) =>
            location.plotGroupId === plotGroupId &&
            application.assignments[cellId]?.kind === "blocked" &&
            !(
              application.index === applicationIndex &&
              (selectedAssignments.has(cellId) || untreatedIds.has(cellId))
            )
        )
      );
      if (hasUnresolvedCells) return [];
      const byProduct = new Map<string, BiologicalProductTotal>();
      const byApplication = Array.from(
        { length: applicationCount },
        (_, index) => {
          const totals = new Map<string, BiologicalProductTotal>();
          const application = applications.find(
            (entry) => entry.index === index
          );
          locations.forEach((location, cellId) => {
            if (location.plotGroupId !== plotGroupId) return;
            const assignment = application?.assignments[cellId];
            let products: BiologicalPlotProduct[] | undefined;
            if (index === applicationIndex && selectedAssignments.has(cellId)) {
              products = selectedAssignments.get(cellId);
            } else if (
              index === applicationIndex &&
              (untreatedIds.has(cellId) ||
                (assignment?.kind === "assigned" &&
                  assignment.treatmentId === treatmentIndex))
            ) {
              return;
            } else if (assignment?.kind === "assigned") {
              products =
                savedProducts(assignment.raw) ??
                getProductsForTreatment(
                  treatments,
                  assignment.treatmentId,
                  location.areaHa
                );
            }
            products?.forEach((product) => {
              addTotal(totals, product, location.replicateMultiplier);
              addTotal(byProduct, product, location.replicateMultiplier);
            });
          });
          return [...totals.values()];
        }
      );
      return [
        {
          plot_group_id: plotGroupId,
          by_product: [...byProduct.values()],
          by_application: byApplication,
        },
      ];
    });
};

export const buildBiologicalAssignmentsSnapshot = ({
  designGroups,
  trialPlots,
  applications,
  treatments,
  throughApplicationIndex,
}: BuildBiologicalAssignmentsSnapshotArgs): BiologicalTreatmentAssignmentsPayload => {
  const locations = buildTargetLocations(designGroups, trialPlots);
  const savedApplications =
    throughApplicationIndex === undefined
      ? applications
      : applications.filter((entry) => entry.index <= throughApplicationIndex);
  const biologicalAssignments = savedApplications.flatMap((application) => {
    const byTreatment = new Map<number, BiologicalPlotAssignment[]>();
    const untreated: BiologicalUntreatedPlot[] = [];
    locations.forEach((location, cellId) => {
      const assignment = application.assignments[cellId];
      const target = {
        plot_group_id: location.plotGroupId,
        plot_cell_id: location.plotCellId,
      };
      if (assignment?.kind === "blocked") {
        throw new Error(
          `Resolve biological assignment for ${cellId} before saving.`
        );
      }
      if (assignment?.kind === "untreated") {
        untreated.push(target);
      } else if (assignment?.kind === "assigned") {
        const raw = assignment.raw;
        const color =
          typeof raw === "object" &&
          raw !== null &&
          "color" in raw &&
          typeof raw.color === "string"
            ? raw.color
            : "";
        const entries = byTreatment.get(assignment.treatmentId) ?? [];
        entries.push({
          ...target,
          color,
          products:
            savedProducts(raw) ??
            getProductsForTreatment(
              treatments,
              assignment.treatmentId,
              location.areaHa
            ),
        });
        byTreatment.set(assignment.treatmentId, entries);
      }
    });
    if (byTreatment.size === 0 && untreated.length === 0) return [];
    return [
      {
        application_index: application.index,
        ...(byTreatment.size > 0
          ? {
              treatments: [...byTreatment].map(
                ([treatmentIndex, assignments]) => ({
                  treatment_index: treatmentIndex,
                  assignments,
                })
              ),
            }
          : {}),
        ...(untreated.length > 0 ? { untreated } : {}),
      },
    ];
  });
  if (biologicalAssignments.length === 0) {
    throw new Error("Assign biological treatments before saving.");
  }
  return {
    biological_treatment_assignments: biologicalAssignments,
    // Totals replace the full stored schedule, even when assignments are cumulative.
    biological_calculated_totals: buildCalculatedTotals(
      locations,
      applications,
      treatments,
      undefined,
      undefined,
      new Map(),
      new Set(),
      [...locations.values()].map((location) => location.plotGroupId)
    ),
  };
};

interface BuildBiologicalResetArgs
  extends Omit<
    BuildBiologicalAssignmentsSnapshotArgs,
    "throughApplicationIndex"
  > {
  target?: { applicationIndex: number; treatmentIndex: number };
}

export const buildBiologicalReset = ({
  designGroups,
  trialPlots,
  applications,
  treatments,
  target,
}: BuildBiologicalResetArgs): {
  applications: NormalizedCpApplication[];
  payload: BiologicalTreatmentAssignmentsPayload;
} => {
  const nextApplications = applications.map((application) => ({
    ...application,
    assignments: Object.fromEntries(
      Object.entries(application.assignments).map(([id, assignment]) => [
        id,
        !target ||
        (application.index === target.applicationIndex &&
          assignment.kind === "assigned" &&
          assignment.treatmentId === target.treatmentIndex)
          ? { kind: "missing" as const }
          : assignment,
      ])
    ),
  }));
  const locations = buildTargetLocations(designGroups, trialPlots);
  return {
    applications: nextApplications,
    payload: {
      biological_treatment_assignments: applications
        .filter(
          (application) =>
            !target || application.index === target.applicationIndex
        )
        .map((application) => ({
          application_index: application.index,
          treatments: (target
            ? [target.treatmentIndex]
            : treatments.map(({ treatmentId }) => treatmentId)
          ).map((treatmentIndex) => ({
            treatment_index: treatmentIndex,
            assignments: [],
          })),
          ...(!target ? { reset_untreated: true } : {}),
          ...(!target ? { untreated: [] } : {}),
        })),
      biological_calculated_totals: buildCalculatedTotals(
        locations,
        nextApplications,
        treatments,
        undefined,
        undefined,
        new Map(),
        new Set(),
        [...locations.values()].map((location) => location.plotGroupId)
      ),
    },
  };
};

export const buildBiologicalTreatmentAssignmentsPayload = ({
  designGroups,
  trialPlots,
  applications,
  treatments,
  applicationIndex,
  treatmentIndex,
  selectedCellIds,
  untreatedCellIds = [],
  color,
}: BuildBiologicalAssignmentsPayloadArgs): BiologicalTreatmentAssignmentsPayload => {
  const locations = buildTargetLocations(designGroups, trialPlots);
  const selectedAssignments = selectedCellIds.map((cellId) => {
    const location = locations.get(cellId);
    if (!location) {
      throw new Error(`Unknown biological plot cell: ${cellId}`);
    }

    return {
      plot_group_id: location.plotGroupId,
      plot_cell_id: location.plotCellId,
      color,
      products: getProductsForTreatment(
        treatments,
        treatmentIndex,
        location.areaHa
      ),
    };
  });
  const untreated = [...new Set(untreatedCellIds)].map((cellId) => {
    const location = locations.get(cellId);
    if (!location) {
      throw new Error(`Unknown untreated plot cell: ${cellId}`);
    }

    return {
      plot_group_id: location.plotGroupId,
      plot_cell_id: location.plotCellId,
    };
  });

  if (selectedAssignments.length === 0 && untreated.length === 0) {
    throw new Error("A biological assignment save must include selected cells");
  }

  const calculatedTotals = buildCalculatedTotals(
    locations,
    applications,
    treatments,
    applicationIndex,
    treatmentIndex,
    new Map(
      selectedCellIds.map((cellId, index) => [
        cellId,
        selectedAssignments[index].products,
      ])
    ),
    new Set(untreatedCellIds)
  );
  return {
    ...(calculatedTotals.length > 0
      ? { biological_calculated_totals: calculatedTotals }
      : {}),
    biological_treatment_assignments: [
      {
        application_index: applicationIndex,
        ...(selectedAssignments.length > 0
          ? {
              treatments: [
                {
                  treatment_index: treatmentIndex,
                  assignments: selectedAssignments,
                },
              ],
            }
          : {}),
        ...(untreated.length > 0 ? { untreated } : {}),
      },
    ],
  };
};
